import { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Card, CardContent } from "@/components/_atoms/card";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { rejoinWorkspaceMembershipMutationDocument } from "../hooks/api/workspace-membership.gql";

import { AvatarWorkspace } from "./avatar-workspace";

export interface WorkspaceSelectProps extends PropsWithChildren {
  text?: string;
}

export const WorkspaceSelect: FC<WorkspaceSelectProps> = () => {
  const { t } = useTranslation("workspace");
  const { t: tCommon } = useTranslation("common");
  const { currentUser, isSuperUser } = useCurrentUserContext();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: { workspaceId: string }) => {
      const client = createOperationClient("rejoinWorkspaceMembership");
      const data = await client.request(rejoinWorkspaceMembershipMutationDocument, { input });
      return data.rejoinWorkspaceMembership;
    },
    onSuccess: (_, variables) => {
      // Invalidate current user query to refresh workspace memberships
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.current(),
      });

      // Invalidate workspace detail query to refresh workspace membership list
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.detail(variables.workspaceId),
      });

      // Invalidate workspace membership list query for this workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.membershipList(variables.workspaceId),
      });

      // Find the workspace and redirect
      const workspace = currentUser?.workspaceMembershipList?.find(
        (wms) => wms.workspaceId === variables.workspaceId,
      )?.workspace;

      if (workspace) {
        window.location.assign(`${window.location.origin}/${workspace.workspaceUrl}`);
      }
    },
    onError: () => {
      toast.error(tCommon("errors.title"), { description: t("errors.genericReload") });
    },
  });

  async function handleJoinWorkspace(workspace: Workspace | null | undefined) {
    if (!workspace || !currentUser) {
      toast.error(tCommon("errors.title"), { description: t("errors.genericReload") });
      return;
    }

    mutation.mutate({
      workspaceId: workspace.id,
    });
  }

  function handleSelectWorkspace(workspace: Workspace | null | undefined) {
    if (!workspace) {
      toast.error(tCommon("errors.title"), { description: t("errors.genericReload") });
      return;
    }

    window.location.assign(`${window.location.origin}/${workspace.workspaceUrl}`);
  }

  if (!currentUser) {
    return (
      <NotFoundView
        text={t("select.noWorkspaceFound")}
      />
    );
  }

  if (!currentUser.workspaceMembershipList) {
    return (
      <NotFoundView
        text={t("select.noWorkspaceFound")}
      />
    );
  }

  return (
    <div className="align-center relative flex h-screen select-none flex-col items-center justify-center overflow-auto">
      <h1 className="my-6 text-center text-xl">{t("select.title")}</h1>

      <Card className="z-10 w-full max-w-xl overflow-hidden overflow-y-scroll border p-10 shadow-xl">
        <CardContent>
          <div className="flex flex-col gap-4">
            {currentUser.workspaceMembershipList
              .slice()
              .sort((a) => (a.status === "ACTIVE" ? -1 : 0))
              .map((workspaceMembership) => {
                if (!workspaceMembership.workspace) {
                  return <></>;
                }

                return (
                  <div
                    key={`${workspaceMembership.userId}+${workspaceMembership.workspaceId}`}
                    className="align-start flex flex-row justify-between gap-4 border-b pb-4"
                  >
                    <div className="h-8 w-8">
                      <AvatarWorkspace workspace={workspaceMembership.workspace} className="h-8 w-8 text-lg" />
                    </div>
                    <div className="text-md flex grow">
                      <span>{workspaceMembership.workspace.name}</span>
                    </div>
                    <div>
                      {workspaceMembership.status === "LEFT" && (
                        <Button variant="outline" onClick={() => handleJoinWorkspace(workspaceMembership.workspace)}>
                          {t("select.join")}
                        </Button>
                      )}
                      {(workspaceMembership.status === "ACTIVE" ||
                        (workspaceMembership.status === "SUSPENDED" && isSuperUser)) && (
                        <Button variant="outline" onClick={() => handleSelectWorkspace(workspaceMembership.workspace)}>
                          {t("select.choose")}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

            <div className="mt-6 w-full">
              <p className="text-sm font-light text-foreground">
                {t("select.loggedInAs")} <span className="font-medium">{currentUser.email}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
